import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { api } from "../api";
import { useSession } from "../App";
import type { Conversation, Locale, RealtimeMessage } from "../types";
import { clearNativeMessageNotification, showNativeMessageNotification } from "../native";

type ChangePage = { changes: RealtimeMessage[]; nextCursor: number; hasMore: boolean };

export function MessageRealtimeBridge() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const session = useSession();
  const locale: Locale = location.pathname.startsWith("/vn/") ? "vn" : "en";

  useEffect(() => {
    const userId = session.data?.user?.id;
    if (!userId) return;
    const cursorKey = `nearby-heart-message-cursor:${userId}`;
    let stopped = false;
    let running: Promise<void> | null = null;
    const sync = () => {
      if (running) return running;
      running = (async () => {
        const stored = localStorage.getItem(cursorKey);
        let cursor = stored === null ? undefined : Math.max(0, Number(stored) || 0);
        let latestAuto: RealtimeMessage | undefined;
        for (let page = 0; page < 10 && !stopped; page += 1) {
          const response = await api<ChangePage>(
            `/messages/changes${cursor === undefined ? "" : `?afterEventSeq=${cursor}`}`,
          );
          for (const change of response.changes) {
            if (change.sender_kind === "auto") latestAuto = change;
            void queryClient.invalidateQueries({ queryKey: ["messages", change.conversation_id] });
          }
          cursor = response.nextCursor;
          localStorage.setItem(cursorKey, String(cursor));
          if (!response.hasMore) break;
        }
        await queryClient.invalidateQueries({ queryKey: ["conversations", locale] });
        if (latestAuto) {
          const cached = queryClient.getQueryData<{ conversations: Conversation[] }>(["conversations", locale]);
          const senderName = cached?.conversations.find((item) => item.id === latestAuto!.conversation_id)?.name;
          await showNativeMessageNotification(latestAuto, locale, senderName);
        }
      })().catch(() => undefined).finally(() => { running = null; });
      return running;
    };
    const socket = io({ path: "/dating-v1/socket.io", withCredentials: true, reconnection: true });
    socket.on("message.created", sync);
    socket.on("connect", sync);
    const foreground = () => { if (!document.hidden) void sync(); };
    window.addEventListener("online", foreground);
    window.addEventListener("focus", foreground);
    document.addEventListener("visibilitychange", foreground);
    const fallback = window.setInterval(() => void sync(), 5000);
    void sync();
    return () => {
      stopped = true;
      window.clearInterval(fallback);
      window.removeEventListener("online", foreground);
      window.removeEventListener("focus", foreground);
      document.removeEventListener("visibilitychange", foreground);
      socket.close();
    };
  }, [locale, queryClient, session.data?.user?.id]);

  useEffect(() => {
    const match = location.pathname.match(/\/chat\/([0-9a-f-]{36})\/?$/i);
    if (match) void clearNativeMessageNotification(match[1]);
  }, [location.pathname]);

  return null;
}
